'use client'

import ThreadTemplate from '@/components/DashboardV2/ThreadTemplate'
import { ListPageTemplate } from '@/components/ListPageTemplate/ListPageTemplate'
import {
  useDeleteSessionMutation,
  useGetAllUserSessionsQuery,
  useSetSessionBookmarkMutation,
  useUpdateSessionMutation,
} from '@/lib/features/session/sessionApi'
import { PAGE_PATH } from '@/lib/page-paths'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { CopilotSession } from 'sprites-types'

const PAGE_SIZE = 20

export default function ThreadsClient() {
  const [offset, setOffset] = useState(0)
  const [allLoadedSessions, setAllLoadedSessions] = useState<CopilotSession[]>([])

  const {
    data: sessionData,
    isError: isSessionsError,
    isLoading: isLoadingSessions,
    isFetching: isFetchingSessions,
  } = useGetAllUserSessionsQuery({ limit: PAGE_SIZE, offset })

  // Update sessions when data arrives (both initial load and pagination)
  useEffect(() => {
    if (!sessionData?.sessions) return

    if (offset === 0) {
      // Initial load or filter reset - replace all sessions
      setAllLoadedSessions(sessionData.sessions)
    } else {
      // Pagination - merge new sessions
      setAllLoadedSessions((prev) => {
        const sessionMap = new Map(prev.map((s) => [s.id, s]))
        sessionData.sessions.forEach((session) => {
          sessionMap.set(session.id, session)
        })
        return Array.from(sessionMap.values())
      })
    }
  }, [sessionData?.sessions, offset])

  const [deleteSession] = useDeleteSessionMutation()
  const [updateSession] = useUpdateSessionMutation()
  const [setSessionBookmark] = useSetSessionBookmarkMutation()

  const handleDeleteSession = useCallback(
    (sessionId: string) => async () => {
      try {
        await deleteSession(sessionId).unwrap()
        toast.success('Chat deleted successfully')
      } catch (error) {
        console.error('Failed to delete chat:', error)
        toast.error('Failed to delete chat. Please try again.')
      }
    },
    [deleteSession]
  )

  const handleRenameSession = useCallback(
    (sessionId: string) => {
      return async (newName: string) => {
        try {
          await updateSession({
            sessionId,
            name: newName,
          }).unwrap()
          toast.success('Chat renamed successfully')
        } catch (error) {
          console.error('Failed to rename chat:', error)
          toast.error('Failed to rename chat. Please try again.')
        }
      }
    },
    [updateSession]
  )

  const handleLoadMore = useCallback(() => {
    setOffset((prev) => prev + PAGE_SIZE)
  }, [])

  return (
    <ListPageTemplate
      title="Chats"
      items={allLoadedSessions}
      isLoading={isLoadingSessions}
      isError={isSessionsError}
      isFetching={isFetchingSessions}
      pagination={sessionData?.pagination}
      onLoadMore={handleLoadMore}
      getItemName={(session) => session.threadSummary || 'Untitled Chat'}
      emptyStateMessage="No chats yet"
      emptyStateSubMessage="Chats you create will appear here"
      errorMessage="Failed to load chats. Please try again later."
      _itemTypeSingular="chat"
      itemTypePlural="chats"
      maxColumnCount={1}
      renderItem={(session) => (
        <ThreadTemplate
          id={session.id}
          title={session.threadSummary || 'Untitled Chat'}
          description=""
          updatedAt={session.updatedAt}
          href={`${PAGE_PATH.COPILOT}/${session.id}`}
          onDeleteAction={handleDeleteSession(session.id)}
          onRenameAction={handleRenameSession(session.id)}
          isBookmarked={session.isBookmarked}
          onBookmarkToggle={async () => {
            const newBookmarkState = !session.isBookmarked
            // Update local state immediately for instant feedback
            setAllLoadedSessions((prev) =>
              prev.map((s) => (s.id === session.id ? { ...s, isBookmarked: newBookmarkState } : s))
            )
            try {
              await setSessionBookmark({
                sessionId: session.id,
                bookmarked: newBookmarkState,
              }).unwrap()
            } catch (error) {
              // Revert on error
              setAllLoadedSessions((prev) =>
                prev.map((s) =>
                  s.id === session.id ? { ...s, isBookmarked: !newBookmarkState } : s
                )
              )
              throw error
            }
          }}
          workspaceId={session.workspaceId}
        />
      )}
    />
  )
}
